/**
 * POILayerMgr.tsx — Points of Interest layer manager (MAP-03 + T-012/W3 + T-170 + T-179)
 *
 * Independent layer module: fetches /api/poi/search (AWS Location Service SearchNearby,
 * categorized server-side into 12 canonical categories — T-170) when an active location is
 * set and renders the results as category-icon markers.
 *
 * T-179 — icon markers + category-aware density (M3):
 *  - Each POI category renders as a pre-composited "map pin chip" (category-color disc +
 *    white Maki glyph + white ring), swapping the old flat circle dots for icons. The glyph
 *    is a redundant semantic channel on top of the color (a color-blind accessibility win).
 *  - Density: one NATIVE-CLUSTERED geojson source PER category, so nearby same-category POIs
 *    collapse into a single chip carrying a count badge ("8") — you see how many *similar*
 *    POIs are under a marker. A dense mixed pile reads as its parts ("Restaurant 8", "Café
 *    4", "Shop 2"). Single POIs render unbadged. Clicking a cluster lists the POIs inside it
 *    and makes no camera movement at all (T-245 acceptance 4). This is all native MapLibre —
 *    no runtime dependency, no /api/poi change.
 *  - `enabledCategories` + `visible` drive per-category layer visibility (a hidden category
 *    simply doesn't render — counts reflect only enabled categories).
 *  - `POI_MIN_ZOOM` native minzoom gate + zoom-aware fetch radius + debounced refetch
 *    (AbortController cancels stale in-flight requests) are unchanged from T-114.
 *
 * The API returns { total, pois: [{ id, category, name, address, lat, lng, dist }] }.
 */
import { useEffect, useRef } from 'react'
import maplibregl from 'maplibre-gl'
import type { GeoJSONSource, MapMouseEvent } from 'maplibre-gl'
import { useMap } from './MapContext'
import { openMarkerPopup, closeMarkerPopup } from './markerPopup'
import { toast } from '../ui/toaster'
import { authedFetch } from '../../lib/api'
import { isMapAlive } from '../../lib/mapLiveness'
import {
  CATEGORY_COLORS,
  POI_CATEGORY_NAMES,
  poiCategorySlug,
  poiImageId,
  registerPoiMarkerImages,
  removePoiMarkerImages,
} from './poiMarkers'
import { padBbox, shouldFetch, withinAreaGate, mergePois, prunePois, type Bbox } from './poiExtent'

// Re-export the color/category surface from its owning module so existing consumers
// (LayersControl legend, MapShell) keep importing it from here.
export { CATEGORY_COLORS, POI_CATEGORY_NAMES }

export interface POILayerMgrProps {
  location: { lat: number; lng: number } | null
  visible?: boolean
  enabledCategories: string[]
}

/** Per-category clustered source id. */
export function poiSourceId(category: string): string {
  return `poi-src-${poiCategorySlug(category)}`
}

/** Per-category symbol layer id. */
export function poiLayerId(category: string): string {
  return `poi-layer-${poiCategorySlug(category)}`
}

// Native clustering: collapse nearby SAME-category POIs into one count chip. clusterMaxZoom
// keeps clusters active across the zoom band we render in (≥ POI_MIN_ZOOM) so dense piles
// show counts, then break apart as the user zooms further in.
const CLUSTER_RADIUS = 40
// Exported as the accepted clustering-zoom baseline so the EV layer can align to it and a
// parity test can assert the two never drift apart again (T-214 clustering flag).
export const CLUSTER_MAX_ZOOM = 18

interface NormalizedPOI {
  id: number
  category: string
  name?: string
  address?: string
  lat: number
  lng: number
}

/**
 * T-245: the fetch extent is derived from the VISIBLE BOUNDS, not a fixed radius.
 * `map.getBounds()` already reflects the canvas size, so a wide monitor produces a wide fetch
 * box — which is the whole point of the card (the old fixed 1500 m circle left the edges of a
 * large screen empty). See poiExtent.ts for the pure logic.
 */
function visibleBbox(map: maplibregl.Map): Bbox {
  const b = map.getBounds()
  return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
}

/**
 * T-114 — minimum zoom at which we fetch/show POIs (Google-Maps-style gating).
 *
 * WHY: `/api/poi/search` proxies a nearby-search under a bounded budget; a fresh query at the
 * larger low-zoom radii routinely exceeds it and returns 502, which the layer surfaces as an
 * error toast and NEVER renders as pins — read by users as "POIs don't work". Empirically
 * (task T-114 timing evidence): r=3300 (zoom 13-14) blew the budget ~half the time; r=1500
 * (zoom ≥15) stayed comfortable. So we only fetch/render at zoom ≥ 15 (radius is always 1500
 * here) and disable the POI toggle below it. Mirrors the ROADS_MIN_ZOOM self-gate pattern.
 */
export const POI_MIN_ZOOM = 15

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// Split fetched POIs into a FeatureCollection per category and setData its clustered source.
// Unknown categories (not in the canonical 12) are dropped — mirrors the server's
// categorize-and-drop (T-170).
function distributePoisToSources(map: maplibregl.Map, pois: NormalizedPOI[]): void {
  const byCat: Record<string, GeoJSON.Feature[]> = {}
  for (const cat of POI_CATEGORY_NAMES) byCat[cat] = []
  for (const p of pois) {
    if (typeof p.lat !== 'number' || typeof p.lng !== 'number') continue
    const bucket = byCat[p.category]
    if (!bucket) continue
    bucket.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
      properties: {
        id: p.id,
        category: p.category,
        name: p.name ?? '',
        address: p.address ?? '',
      },
    })
  }
  for (const cat of POI_CATEGORY_NAMES) {
    const source = map.getSource(poiSourceId(cat)) as GeoJSONSource | undefined
    source?.setData({ type: 'FeatureCollection', features: byCat[cat]! })
  }
}

// A hidden category simply isn't visible; a layer shows iff the whole POI layer is visible
// AND its category is enabled. Both the visibility toggle and the category filter run this.
function applyLayerVisibility(map: maplibregl.Map, visible: boolean, enabled: string[]): void {
  for (const cat of POI_CATEGORY_NAMES) {
    const layerId = poiLayerId(cat)
    if (!map.getLayer(layerId)) continue
    const show = visible && enabled.includes(cat)
    map.setLayoutProperty(layerId, 'visibility', show ? 'visible' : 'none')
  }
}

function openPoiPopup(
  map: maplibregl.Map,
  lngLat: maplibregl.LngLatLike,
  p: Record<string, unknown>,
): void {
  openMarkerPopup(map, {
    lngLat,
    maxWidth: '220px',
    html: `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px">
        <p style="font-weight:700;margin:0">${esc(p['name'] || 'Point of Interest')}</p>
        <p style="color:var(--muted-foreground);margin:2px 0;font-size:11px">${esc(p['category'])}</p>
        ${p['address'] ? `<p style="color:var(--muted-foreground);margin:2px 0;font-size:10px">${esc(p['address'])}</p>` : ''}
      </div>`,
  })
}

/**
 * How many cluster leaves a popup will list. A cluster can hold more POIs than is sensible to
 * render in a popup, so the list is capped — and the header SAYS SO when it truncates, rather
 * than quietly showing a prefix as if it were the whole set. The list scrolls within the popup,
 * so the cap (not the popup height) is the only limit.
 */
export const CLUSTER_LEAVES_LIMIT = 100

// Clicking a cluster lists the POIs inside it — reuses the popup surface, adds no dependency.
// (A "spiderfy" fan-out would need a plugin; deliberately avoided.)
function openClusterLeavesPopup(
  map: maplibregl.Map,
  lngLat: maplibregl.LngLatLike,
  leaves: Array<{ properties?: Record<string, unknown> | null }>,
  totalCount?: number,
): void {
  const items = leaves
    .map((l) => {
      const lp = l.properties ?? {}
      return `<li style="margin:2px 0;list-style:none">
        <span style="font-weight:600">${esc(lp['name'] || 'Point of Interest')}</span>
        <span style="color:var(--muted-foreground)"> · ${esc(lp['category'])}</span>
      </li>`
    })
    .join('')
  const total = totalCount ?? leaves.length
  const truncated = total > leaves.length
  openMarkerPopup(map, {
    lngLat,
    maxWidth: '240px',
    html: `<div style="font-family:Inter,system-ui,sans-serif;font-size:12px">
        <p style="font-weight:700;margin:0 0 4px">${total} nearby</p>
        ${truncated ? `<p style="color:var(--muted-foreground);margin:0 0 4px;font-size:10px">showing the first ${leaves.length}</p>` : ''}
        <ul style="margin:0;padding:0;max-height:220px;overflow-y:auto">${items}</ul>
      </div>`,
  })
}

/**
 * Click anywhere on the map — resolves to AT MOST ONE POI marker (T-435).
 *
 * WHY THIS IS ONE MAP-LEVEL HANDLER AND NOT TWELVE LAYER-SCOPED ONES. Each category owns its own
 * layer, and MapLibre invokes a layer-scoped click handler for EVERY layer under the cursor. With
 * one handler per category, a click where a single POI overlapped a cluster ran two handlers that
 * could not see each other, and each opened a popup — the stacked pair in Ricardo's screenshot.
 * Coordinating twelve handlers after the fact would only ever produce a "last one wins" rule whose
 * winner depends on binding order and on which path happens to be async. Asking ONCE, for what is
 * actually rendered under the cursor, replaces that with the only answer that matches what the
 * user sees — the marker on top.
 *
 * `queryRenderedFeatures` is documented to return "the topmost rendered feature first ... sorted by
 * descending z-order", and it already excludes hidden categories and layers outside their zoom
 * range — so the category filter and the POI_MIN_ZOOM gate keep working with no extra bookkeeping.
 *
 * Cluster → list the POIs inside it, and NOTHING else (T-245 acceptance 4). Clicking a
 * decluttered marker means "what's under here?", so answering it with an unrequested camera
 * jump is a defect, not a feature: the map moves out from under you when all you asked for was
 * the detail — which the list already gives. Zoom stays available through normal map gestures.
 * (Previously this eased to getClusterExpansionZoom and only fell back to the list once the
 * cluster could no longer expand — i.e. zoom-first, list-later. Kept on the record so the
 * camera jump does not get reintroduced as an "improvement".)
 *
 * Single POI → the name/category/address popup, unchanged.
 */
function makePoiMapClick(map: maplibregl.Map, clickSeq: { current: number }) {
  return (e: MapMouseEvent): void => {
    // Only query layers that exist: queryRenderedFeatures throws on an unknown layer id, and the
    // style can be mid-teardown or mid-basemap-swap when a click lands.
    const layers = POI_CATEGORY_NAMES.map(poiLayerId).filter((id) => map.getLayer(id))
    if (layers.length === 0) return
    const top = map.queryRenderedFeatures(e.point, { layers })[0]
    if (!top) return

    const props = (top.properties ?? {}) as Record<string, unknown>
    const geom = top.geometry as GeoJSON.Point | undefined
    const coords = (geom?.coordinates as [number, number] | undefined) ?? [
      e.lngLat.lng,
      e.lngLat.lat,
    ]

    // Every click gets a number so an async continuation can tell whether it is still the click
    // the user is waiting on.
    const seq = ++clickSeq.current

    if (props['cluster']) {
      const source = map.getSource(top.source) as GeoJSONSource | undefined
      if (!source) return
      const clusterId = props['cluster_id'] as number
      const pointCount = typeof props['point_count'] === 'number' ? props['point_count'] : undefined
      Promise.resolve(source.getClusterLeaves(clusterId, CLUSTER_LEAVES_LIMIT, 0))
        .then((leaves) => {
          // The leaves lookup is async, so a slow one from an earlier click could otherwise land
          // on top of the popup the user is looking at now. Drop it if the click has moved on.
          if (seq !== clickSeq.current) return
          openClusterLeavesPopup(
            map,
            coords,
            leaves as Array<{ properties?: Record<string, unknown> | null }>,
            pointCount,
          )
        })
        .catch(() => {
          /* cluster metadata unavailable — nothing to list */
        })
      return
    }

    openPoiPopup(map, e.lngLat, props)
  }
}

export function POILayerMgr({
  location,
  visible = true,
  enabledCategories,
}: POILayerMgrProps): null {
  const map = useMap()
  const initializedRef = useRef(false)
  // T-245: the accumulated POI store (keyed by id) and the extent it was fetched for. Together
  // these replace the old "refetch around the new centre and setData the response" model, which
  // erased any marker that fell out of the fresh nearest-N even while it was still on screen.
  const storeRef = useRef<Map<number, NormalizedPOI>>(new Map())
  const lastFetchedBboxRef = useRef<Bbox | null>(null)
  // F3/T-082: reassigned by each location effect so a persistent error toast's Retry
  // always refetches the CURRENT location, never the stale one it was raised for.
  const retryRef = useRef<() => void>(() => {})
  // Latest visible/enabled, read by the initial visibility apply in the setup effect.
  const visibleRef = useRef(visible)
  const enabledRef = useRef(enabledCategories)

  // ── One-time sources + layers setup (one clustered source + symbol layer per category) ──
  useEffect(() => {
    if (!map || initializedRef.current) return
    initializedRef.current = true

    // Register the 12 chip images (browser-only; a no-op in jsdom/SSR). Icons attach to the
    // layers as they rasterize — MapLibre tolerates a not-yet-added icon-image.
    void registerPoiMarkerImages(map)

    // T-435: ONE click listener for all categories (see makePoiMapClick). Hover stays per-layer —
    // the cursor genuinely is a per-layer concern and cannot double-open anything.
    const clickSeq = { current: 0 }
    const onMapClick = makePoiMapClick(map, clickSeq)
    map.on('click', onMapClick)

    const bindings: Array<{
      layerId: string
      onEnter: () => void
      onLeave: () => void
    }> = []

    for (const cat of POI_CATEGORY_NAMES) {
      const sourceId = poiSourceId(cat)
      const layerId = poiLayerId(cat)

      map.addSource(sourceId, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterRadius: CLUSTER_RADIUS,
        clusterMaxZoom: CLUSTER_MAX_ZOOM,
      })

      map.addLayer({
        id: layerId,
        type: 'symbol',
        source: sourceId,
        // T-114: native gate so stale POIs never draw below the fetch threshold.
        minzoom: POI_MIN_ZOOM,
        layout: {
          'icon-image': poiImageId(cat),
          'icon-size': 1,
          // Density is expressed by clustering + count badges, not collision-hiding, so
          // every category chip stays visible (that IS the "show many similar" signal).
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          // Count badge from the native cluster point_count → single POIs render unbadged.
          'text-field': ['case', ['has', 'point_count'], ['to-string', ['get', 'point_count']], ''],
          'text-font': ['Open Sans Bold', 'Open Sans Regular'],
          'text-size': 12,
          'text-offset': [0.85, -0.85],
          'text-anchor': 'center',
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#0f172a',
          'text-halo-width': 2,
        },
      })

      const onEnter = () => {
        map.getCanvas().style.cursor = 'pointer'
      }
      const onLeave = () => {
        map.getCanvas().style.cursor = ''
      }
      map.on('mouseenter', layerId, onEnter)
      map.on('mouseleave', layerId, onLeave)
      bindings.push({ layerId, onEnter, onLeave })
    }

    // Apply the initial visible/enabled state now that the layers exist.
    applyLayerVisibility(map, visibleRef.current, enabledRef.current)

    return () => {
      initializedRef.current = false
      map.off('click', onMapClick)
      // A popup outlives the layer that opened it, so unmounting without this leaves an orphan
      // anchored to markers that are gone.
      closeMarkerPopup(map)
      for (const b of bindings) {
        map.off('mouseenter', b.layerId, b.onEnter)
        map.off('mouseleave', b.layerId, b.onLeave)
      }
      // BEAD-0037: skip layer/source teardown if MapView's map.remove() already ran on
      // sign-out (getLayer would throw on the dropped style and blank the app).
      if (isMapAlive(map)) {
        for (const cat of POI_CATEGORY_NAMES) {
          const layerId = poiLayerId(cat)
          const sourceId = poiSourceId(cat)
          if (map.getLayer(layerId)) map.removeLayer(layerId)
          if (map.getSource(sourceId)) map.removeSource(sourceId)
        }
        removePoiMarkerImages(map)
      }
    }
  }, [map])

  // ── Category filter + visibility toggle (both drive per-layer visibility) ───────────────
  useEffect(() => {
    visibleRef.current = visible
    enabledRef.current = enabledCategories
    if (!map || !initializedRef.current) return
    applyLayerVisibility(map, visible, enabledCategories)
  }, [map, visible, enabledCategories])

  // ── Fetch POIs for the VISIBLE VIEWPORT + debounced refetch on map move (T-245) ─────────
  useEffect(() => {
    if (!map || !location) return
    let controller = new AbortController()
    let debounce: ReturnType<typeof setTimeout> | null = null

    // A location change means a new place: start the accumulation fresh rather than carrying
    // markers from wherever the user was before.
    storeRef.current = new Map()
    lastFetchedBboxRef.current = null

    const fetchPOIs = (opts: { force?: boolean } = {}) => {
      // T-114 zoom floor, kept as a cheap short-circuit ahead of the (more meaningful) area gate.
      if (map.getZoom() < POI_MIN_ZOOM) return

      const visible = visibleBbox(map)
      const target = padBbox(visible)

      // T-245 density bound: gate on the GROUND SIZE of the box, not on zoom. Zoom alone is
      // screen-size blind — the very defect this card fixes — so a 4K monitor at a "safe" zoom
      // could still ask for a metro-sized area.
      if (!withinAreaGate(target)) return

      // T-245 fetched-extent skip: while the viewport stays inside what we already fetched there
      // is nothing new to ask for. A small pan therefore issues ZERO requests — and because
      // nothing is replaced, it cannot drop a marker that is still on screen.
      if (!opts.force && !shouldFetch({ visible, lastFetched: lastFetchedBboxRef.current })) return

      controller.abort()
      controller = new AbortController()
      const { signal } = controller

      const params = new URLSearchParams({ bbox: target.join(',') })
      authedFetch(`/api/poi/search?${params}`, { signal })
        .then((r) => {
          // F8: a continuation that resolves after teardown/location-change must not
          // touch the (possibly removed) sources. Matches County/Tract/Roads layers.
          if (signal.aborted) return null
          // A 504/5xx returns an HTML body that throws in .json() — check ok first so
          // the failure is a clear, user-visible toast instead of a swallowed parse error.
          if (!r.ok) throw new Error(`POI fetch failed: ${r.status}`)
          return r.json()
        })
        .then((data: unknown) => {
          if (signal.aborted || !data) return
          const payload = data as { pois?: NormalizedPOI[] }
          // Merge, don't replace — the old setData(only-the-new-response) is exactly what made a
          // slight pan erase markers that were still in view. Prune to the fetched box so the
          // store stays bounded as the user roams.
          storeRef.current = prunePois(mergePois(storeRef.current, payload.pois ?? []), target)
          lastFetchedBboxRef.current = target
          distributePoisToSources(map, [...storeRef.current.values()])
        })
        .catch((err: unknown) => {
          if ((err as Error)?.name === 'AbortError' || signal.aborted) return
          toast.error("Couldn't load nearby points of interest.", {
            id: 'poi-fetch-error', // F7: dedupe — a flapping fetch updates one toast, no stacking
            duration: 8000, // F7: transient — auto-dismiss so it can't linger
            action: { label: 'Retry', onClick: () => retryRef.current() },
          })
        })
    }

    // F3: Retry targets the CURRENT viewport. retryRef is reassigned every effect run, and
    // forces past the extent skip so an errored fetch can actually be retried.
    retryRef.current = () => fetchPOIs({ force: true })
    fetchPOIs({ force: true })

    // Refetch for the new viewport after the user pans/zooms (debounced). Most small pans will
    // short-circuit inside fetchPOIs without touching the network.
    const onMoveEnd = () => {
      if (debounce) clearTimeout(debounce)
      debounce = setTimeout(() => fetchPOIs(), 400)
    }
    map.on('moveend', onMoveEnd)

    return () => {
      controller.abort()
      if (debounce) clearTimeout(debounce)
      map.off('moveend', onMoveEnd)
    }
  }, [map, location])

  return null
}
